/**
 * Referral Tracking Module
 *
 * Story 6.5: Viral Tracking and Attribution
 *
 * Handles capturing and persisting referral attribution from
 * "Made with Boty" links on public bot pages.
 *
 * Storage strategy:
 * - Primary: localStorage (fast, reliable)
 * - Backup: Cookie (survives localStorage clearing)
 * - Expiry: 7 days from capture
 */

const STORAGE_KEY = 'boty_referral';
const COOKIE_NAME = 'boty_referral';
const EXPIRY_DAYS = 7;

export interface ReferralData {
  botId: string;
  variant?: string; // Story 6.11: Viral CTA variant that drove the referral
  capturedAt: number; // Unix timestamp in milliseconds
}

/**
 * Check if we're in a browser environment
 */
function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

/**
 * Calculate expiry date for cookies
 */
function getExpiryDate(): Date {
  const date = new Date();
  date.setDate(date.getDate() + EXPIRY_DAYS);
  return date;
}

/**
 * Check if referral data has expired
 */
function isExpired(data: ReferralData): boolean {
  const expiryTime = data.capturedAt + EXPIRY_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() > expiryTime;
}

/**
 * Set a cookie with the referral data
 */
function setCookie(data: ReferralData): void {
  if (!isBrowser()) return;

  const expires = getExpiryDate().toUTCString();
  const value = JSON.stringify(data);
  // Set cookie with secure attributes (Secure flag in production)
  const secure = process.env.NODE_ENV === 'production' ? '; Secure' : '';
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax${secure}`;
}

/**
 * Get referral data from cookie
 */
function getCookie(): ReferralData | null {
  if (!isBrowser()) return null;

  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === COOKIE_NAME && value) {
      try {
        return JSON.parse(decodeURIComponent(value));
      } catch {
        return null;
      }
    }
  }
  return null;
}

/**
 * Delete the referral cookie
 */
function deleteCookie(): void {
  if (!isBrowser()) return;
  document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

/**
 * Capture and store referral attribution from a bot ID.
 * Stores in both localStorage and cookie for redundancy.
 *
 * @param botId - The referring bot's ID from the URL parameter
 * @param variant - Story 6.11: The viral CTA variant that drove the click
 */
export function captureReferral(botId: string, variant?: string): void {
  if (!isBrowser() || !botId) return;

  // Validate UUID format (basic check)
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(botId)) {
    console.warn('[ReferralTracking] Invalid bot ID format:', botId);
    return;
  }

  const data: ReferralData = {
    botId,
    ...(variant && { variant }),
    capturedAt: Date.now(),
  };

  // Store in localStorage
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('[ReferralTracking] Failed to write to localStorage:', e);
  }

  // Backup to cookie
  setCookie(data);

  if (process.env.NODE_ENV === 'development') {
    console.log('[ReferralTracking] Captured referral:', data);
  }
}

/**
 * Retrieve the current referral attribution.
 * Checks localStorage first, then falls back to cookie.
 * Returns null if no referral exists or if it has expired.
 *
 * @returns The referral bot ID if valid and not expired, null otherwise
 */
export function getReferral(): string | null {
  if (!isBrowser()) return null;

  let data: ReferralData | null = null;

  // Try localStorage first
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      data = JSON.parse(stored);
    }
  } catch {
    // Ignore localStorage errors
  }

  // Fall back to cookie if localStorage failed
  if (!data) {
    data = getCookie();
  }

  // Check if data exists and is not expired
  if (data && !isExpired(data)) {
    return data.botId;
  }

  // Clear expired data
  if (data && isExpired(data)) {
    clearReferral();
  }

  return null;
}

/**
 * Get the full referral data including capture timestamp.
 * Useful for debugging and analytics.
 *
 * @returns The full referral data object if valid, null otherwise
 */
export function getReferralData(): ReferralData | null {
  if (!isBrowser()) return null;

  let data: ReferralData | null = null;

  // Try localStorage first
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      data = JSON.parse(stored);
    }
  } catch {
    // Ignore localStorage errors
  }

  // Fall back to cookie
  if (!data) {
    data = getCookie();
  }

  // Return null if expired
  if (data && isExpired(data)) {
    return null;
  }

  return data;
}

/**
 * Clear the referral attribution.
 * Should be called after a bot is created with the referral credited.
 */
export function clearReferral(): void {
  if (!isBrowser()) return;

  // Clear localStorage
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore errors
  }

  // Clear cookie
  deleteCookie();

  if (process.env.NODE_ENV === 'development') {
    console.log('[ReferralTracking] Cleared referral');
  }
}

/**
 * Check if there's an active (non-expired) referral
 */
export function hasReferral(): boolean {
  return getReferral() !== null;
}
